import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFollowupDueCount } from '@/hooks/useFollowupDueCount';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, CalendarClock } from 'lucide-react';

const StudySelector = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const dueCount = useFollowupDueCount();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--paper)' }}>
      <header className="border-b bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
            Clinical Decision Concordance Studies Program
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Button onClick={() => navigate('/followups')} className="gap-2 text-white" style={{ backgroundColor: 'var(--brand)' }}>
              <CalendarClock className="w-4 h-4" />
              Επανεξετάσεις
              {dueCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: 'white', color: 'var(--brand)' }}
                >
                  {dueCount}
                </span>
              )}
            </Button>
            <Button variant="outline" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Επιλέξτε Μελέτη
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto text-balance">
              Καταχωρήστε εάν πρόκειται για αξιολόγηση <strong className="font-bold">ΠΡΙΝ</strong> από κύηση (κατά την προετοιμασία, PRECONCEPTION) ή <strong className="font-bold">ΚΑΤΑ</strong> τη διάρκεια της κύησης (PREGNANCY)
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Preconception Study Card - LEFT */}
            <Card
              className="cursor-pointer bg-white transition-colors duration-150 hover:bg-[#fdfdfb] flex flex-col"
              style={{
                borderColor: 'var(--hairline)',
                borderTopColor: 'var(--arm-preconception)',
                borderTopWidth: '4px',
                borderRadius: '12px'
              }}
              onClick={() => navigate('/preconception')}
            >
              <CardHeader className="pb-4">
                <span
                  className="inline-block self-start text-xs font-semibold uppercase tracking-wide mb-3 px-2.5 py-1 rounded"
                  style={{ color: '#3f4636', backgroundColor: '#eef0e8' }}
                >
                  <strong className="font-bold">Πριν</strong> την κύηση
                </span>
                <CardTitle className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
                  Preconception Study
                </CardTitle>
                <CardDescription className="text-base mt-2" style={{ color: 'var(--ink-muted)' }}>
                  Μελέτη Συμφωνίας για Διαχείριση θυρεοειδικής λειτουργίας <strong className="font-bold">πριν</strong> την Κύηση
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                <Button
                  className="w-full text-white mt-auto"
                  style={{ backgroundColor: 'var(--ink)' }}
                  size="lg"
                >
                  Ξεκινήστε την Αξιολόγηση
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Pregnancy Study Card - RIGHT */}
            <Card
              className="cursor-pointer bg-white transition-colors duration-150 hover:bg-[#fdfdfb] flex flex-col"
              style={{
                borderColor: 'var(--hairline)',
                borderTopColor: 'var(--arm-pregnancy)',
                borderTopWidth: '4px',
                borderRadius: '12px'
              }}
              onClick={() => navigate('/pregnancy')}
            >
              <CardHeader className="pb-4">
                <span
                  className="inline-block self-start text-xs font-semibold uppercase tracking-wide mb-3 px-2.5 py-1 rounded"
                  style={{ color: '#5c352c', backgroundColor: '#F3E5E0' }}
                >
                  <strong className="font-bold">Κατά</strong> την κύηση
                </span>
                <CardTitle className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
                  Pregnancy Study
                </CardTitle>
                <CardDescription className="text-base mt-2" style={{ color: 'var(--ink-muted)' }}>
                  Μελέτη Συμφωνίας για Διαχείριση θυρεοειδικής λειτουργίας <strong className="font-bold">κατά</strong> την Κύηση
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                <Button
                  className="w-full text-white mt-auto"
                  style={{ backgroundColor: 'var(--ink)' }}
                  size="lg"
                >
                  Ξεκινήστε την Αξιολόγηση
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500">
              pyruvate.dev
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudySelector;
